/**
 * Enter в открытом окне нажимает его главную кнопку: «OK», «Дальше»,
 * «Сохранить», «Добавить». Раньше Enter в окнах не делал ничего, и
 * приходилось тянуться к мыши.
 *
 * Главная кнопка — `.wb-btn--primary` (или помеченная `data-enter-default`).
 * Кнопки удаления (`.wb-btn--danger`) по Enter не нажимаются никогда: удалить
 * доску случайным Enter нельзя.
 *
 * Обработчик стоит на всплытии: если поле или кнопка в окне уже сами
 * разобрались с Enter (форма отправилась, кнопка нажалась), он молчит.
 */

/** Окна, которые закрывают собой доску: Enter в них — «согласиться». */
export const MODAL_SELECTOR = 'dialog[open], [aria-modal="true"], .wb-tour';
/** Любые окошки, включая небольшие всплывашки на панелях. */
const ANY_DIALOG_SELECTOR = `${MODAL_SELECTOR}, [role="dialog"], [role="alertdialog"]`;

function isVisible(el: Element): boolean {
  if (!(el instanceof HTMLElement)) return true;
  if (el.hidden) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

/** Самое верхнее открытое окно, которое закрывает доску. */
export function topModal(): Element | null {
  const all = Array.from(document.querySelectorAll(MODAL_SELECTOR)).filter(isVisible);
  return all.length ? all[all.length - 1]! : null;
}

function primaryButton(scope: Element): HTMLElement | null {
  const marked = scope.querySelector<HTMLElement>("[data-enter-default]:not([disabled])");
  if (marked && isVisible(marked)) return marked;
  const candidates = Array.from(
    scope.querySelectorAll<HTMLElement>(".wb-btn--primary:not([disabled])")
  ).filter((b) => isVisible(b) && !b.classList.contains("wb-btn--danger"));
  // Несколько главных кнопок — непонятно, какую нажать; лучше ничего.
  return candidates.length === 1 ? candidates[0]! : null;
}

/** Enter здесь и так что-то делает — не мешаем. */
function enterHandledNatively(target: Element | null): boolean {
  if (!target) return false;
  if (target instanceof HTMLElement && target.isContentEditable) return true;
  const tag = target.tagName;
  if (tag === "TEXTAREA" || tag === "SELECT" || tag === "BUTTON" || tag === "A") return true;
  if (target.closest('[role="button"], [role="option"], [role="menuitem"], [role="radio"], summary')) {
    return true;
  }
  // Поле внутри формы: Enter отправляет форму сам.
  if (tag === "INPUT" && (target as HTMLInputElement).form) return true;
  if (target.closest(".cm-editor")) return true;
  return false;
}

function onKeyDown(ev: KeyboardEvent): void {
  if (ev.key !== "Enter" || ev.defaultPrevented || ev.isComposing) return;
  if (ev.shiftKey || ev.altKey || ev.ctrlKey || ev.metaKey) return;
  const target = ev.target instanceof Element ? ev.target : null;
  let scope = target?.closest(ANY_DIALOG_SELECTOR) ?? null;
  if (scope) {
    // Фокус внутри окошка: поле или кнопка там сами знают, что делать с Enter.
    if (enterHandledNatively(target)) return;
  } else {
    // Фокус снаружи — часто на кнопке, которой окно открыли. Без этого Enter
    // нажимал бы её ещё раз, а не «OK» в окне.
    scope = topModal();
    if (!scope) return;
    if (target instanceof HTMLElement && (target.isContentEditable || target.tagName === "TEXTAREA")) return;
    if (target?.closest(".cm-editor")) return;
  }
  const btn = primaryButton(scope);
  if (!btn) return;
  ev.preventDefault();
  ev.stopPropagation();
  btn.click();
}

let installed = false;

export function installEnterToConfirm(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener("keydown", onKeyDown);
}
